import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Plus, Settings, Trash2, Bot, Plug, Layout, Copy, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmDialog';
import AppHeader from '../components/AppHeader';
import Breadcrumb from '../components/Breadcrumb';
import {
  PageContainer, PageHeader, PageTitle, PageSubtitle, Card, Badge, Button,
  Input, PasswordInput, Select, Label, FormGroup, Grid, Flex, ModalOverlay, ModalBox,
  ModalHeader, ModalTitle, ModalBody, ModalFooter, EmptyState, EmptyIcon, EmptyText
} from '../components/ui';
import { theme } from '../styles/theme';
import { formatPrice } from '../utils/currency';
import api from '../services/api';
import ConnectorTypeForm from '../components/ConnectorTypeForm';
import BrandIcon from '../components/BrandIcon';
import AgentForm from '../components/AgentForm';

const EMPTY_TEMPLATE = { templateId: '', type: '', name: '', icon: '🔌', description: '', displayPhone: '', displayEmail: '', routingKey: '', accessToken: '', credentials: '{}', billingMode: 'platform_credits' };



const SallusMarketplace = () => {
  const { t, i18n } = useTranslation('marketplace');
  const lang = (v) => typeof v === 'object' && v !== null ? (v[i18n.language] || v['pt-BR'] || v['en'] || '') : (v || '');
  const toast = useToast();
  const confirm = useConfirm();
  const TABS = [
    { id: 'agents',     label: t('tabAgents'),     icon: <Bot size={14} /> },
    { id: 'connectors', label: t('tabConnectors'), icon: <Plug size={14} /> },
    { id: 'templates',  label: t('tabTemplates'),  icon: <Layout size={14} /> }
  ];
  const [activeTab, setActiveTab] = useState('agents');
  const [editMode, setEditMode] = useState(false);
  const [agentSearch, setAgentSearch] = useState('');
  const [agentTagFilter, setAgentTagFilter] = useState('');
  const [configuringAgent, setConfiguringAgent] = useState(null);
  const [configuringAgentForm, setConfiguringAgentForm] = useState({});
  const [configuringAgentSaving, setConfiguringAgentSaving] = useState(false);
  const [configuringTemplateConnectors, setConfiguringTemplateConnectors] = useState([]);
  const [creatingAgent, setCreatingAgent] = useState(false);
  const [agentForm, setAgentForm] = useState({});
  const [templateConnectors, setTemplateConnectors] = useState([]); // conectores template do agente global
  const [viewingFunctions, setViewingFunctions] = useState(null);
  const [editingConnectorType, setEditingConnectorType] = useState(null);
  const [deletingConnectorType, setDeletingConnectorType] = useState(null);
  const [creatingConnectorType, setCreatingConnectorType] = useState(false);
  const [deletingAgent, setDeletingAgent] = useState(null);
  const [connectorTypes, setConnectorTypes] = useState([]);
  const [marketplaceAgents, setMarketplaceAgents] = useState([]);
  const [sharedConnectors, setSharedConnectors] = useState([]);
  const [connectorTemplates, setConnectorTemplates] = useState([]);
  const [templateModal, setTemplateModal] = useState(null); // null | 'create' | object(editing)
  const [templateForm, setTemplateForm] = useState(EMPTY_TEMPLATE);
  const [templateSaving, setTemplateSaving] = useState(false);
  const [tiers, setTiers] = useState([]);
  const [copiedId, setCopiedId] = useState(null);

  const copyWebhook = (tpl) => {
    const url = `${process.env.REACT_APP_API_URL}/webhooks/${tpl.type}/${tpl.templateId}`;
    navigator.clipboard.writeText(url);
    setCopiedId(tpl.templateId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const breadcrumbItems = [
    { label: 'Libben Agents', icon: '🚀', path: '/sallus/dashboard' },
    { label: t('title'), icon: '📦' }
  ];

  useEffect(() => {
    const lang = i18n.language;
    api.get('/marketplace/connector-types').then(r => setConnectorTypes(r.data.connectorTypes || [])).catch(console.error);
    api.get(`/marketplace/agents?lang=${lang}`).then(r => setMarketplaceAgents(r.data.agents || [])).catch(console.error);
    api.get('/platform/tiers').then(r => setTiers(r.data.tiers || [])).catch(console.error);
    api.get('/connectors/templates').then(r => setConnectorTemplates(r.data.templates || [])).catch(console.error);
    api.get('/connectors/shared').then(r => setSharedConnectors(r.data.connectors || [])).catch(console.error);
  }, [i18n.language]);

  const loadTemplates = () => api.get('/connectors/templates').then(r => setConnectorTemplates(r.data.templates || [])).catch(console.error);

  const openCreateTemplate = () => { setTemplateForm(EMPTY_TEMPLATE); setTemplateModal('create'); };
  const openEditTemplate = (tpl) => {
    setTemplateForm({
      templateId: tpl.templateId || '',
      type: tpl.type || '',
      name: tpl.name || '',
      icon: tpl.icon || '🔌',
      description: tpl.description || '',
      displayPhone: tpl.displayPhone || '',
      displayEmail: tpl.displayEmail || '',
      routingKey: tpl.routingKey || '',
      accessToken: tpl.accessToken || '',
      credentials: typeof tpl.credentials === 'object' ? JSON.stringify(tpl.credentials, null, 2) : (tpl.credentials || '{}'),
      billingMode: tpl.billingMode || 'platform_credits',
    });
    setTemplateModal(tpl);
  };

  const handleSaveTemplate = async () => {
    if (!templateForm.templateId || !templateForm.type || !templateForm.name) {
      toast.error(t('connTplRequiredFields')); return;
    }
    let parsedCredentials = {};
    try {
      parsedCredentials = JSON.parse(templateForm.credentials || '{}');
    } catch (e) {
      toast.error(t('connTplInvalidCredentials')); return;
    }
    setTemplateSaving(true);
    try {
      const payload = { ...templateForm, credentials: parsedCredentials };
      if (templateModal === 'create') {
        await api.post('/connectors/templates', payload);
        toast.success(t('connTplCreated'));
      } else {
        await api.put(`/connectors/templates/${templateModal.templateId}`, payload);
        toast.success(t('connTplUpdated'));
      }
      setTemplateModal(null); loadTemplates();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
    finally { setTemplateSaving(false); }
  };

  const handleDeleteTemplate = async (tpl) => {
    const ok = await confirm({ title: t('connTplRemoveConfirm', { name: tpl.name }), danger: true });
    if (!ok) return;
    try {
      await api.delete(`/connectors/templates/${tpl.templateId}`);
      toast.success(t('connTplRemoved')); loadTemplates();
    } catch (e) { toast.error(e.response?.data?.error || e.message); }
  };

  const tabCounts = { agents: marketplaceAgents.length, connectors: connectorTypes.length, templates: connectorTemplates.length };

  return (
    <>
      <AppHeader title={t('title')} />
      <BreadcrumbBar><Breadcrumb items={breadcrumbItems} /></BreadcrumbBar>
      <PageContainer>
        <PageHeader>
          <div>
            <PageTitle>{t('title')}</PageTitle>
            <PageSubtitle>{t('subtitle')}</PageSubtitle>
          </div>
          <Flex gap={theme.spacing[2]}>
            {editMode && activeTab === 'agents' && (
              <Button variant="success" size="sm" onClick={() => { setAgentForm({ instanceName: '', customPrompt: '', temperature: 0.7, model: '', icon: '🤖', role: '', description: '', agentId: '', allowedOrigins: [], enabledActions: [] }); setTemplateConnectors([]); setCreatingAgent(true); }}><Plus size={14} /> {t('newAgent')}</Button>
            )}
            {editMode && activeTab === 'connectors' && (
              <Button variant="success" size="sm" onClick={() => setCreatingConnectorType(true)}><Plus size={14} /> {t('newType')}</Button>
            )}
            {editMode && activeTab === 'templates' && (
              <Button variant="success" size="sm" onClick={openCreateTemplate}><Plus size={14} /> {t('connTplNew')}</Button>
            )}
            <Button variant={editMode ? 'primary' : 'secondary'} size="sm" onClick={() => setEditMode(!editMode)}>
              <Settings size={14} /> {editMode ? t('exitEditMode') : t('editMode')}
            </Button>
          </Flex>
        </PageHeader>

        <TabBar>
          {TABS.map(tab => (
            <Tab key={tab.id} active={activeTab === tab.id} onClick={() => setActiveTab(tab.id)}>
              {tab.icon} {tab.label} ({tabCounts[tab.id]})
            </Tab>
          ))}
        </TabBar>

        {activeTab === 'agents' && (() => {
          const CATEGORY_LABELS = {
            saude: t('categories.saude'), atendimento: t('categories.atendimento'), vendas: t('categories.vendas'),
            suporte: t('categories.suporte'), marketing: t('categories.marketing'), financeiro: t('categories.financeiro'),
            operacoes: t('categories.operacoes'), juridico: t('categories.juridico'), rh: t('categories.rh'), ti: t('categories.ti'),
            produtividade: t('categories.produtividade'), imobiliario: t('categories.imobiliario'),
            ecommerce: t('categories.ecommerce'), pessoal: t('categories.pessoal'), geral: t('categories.geral'), interno: t('categories.interno'),
          };
          const CATEGORY_ORDER = [
            'saude', 'atendimento', 'vendas', 'suporte', 'marketing', 'financeiro',
            'operacoes', 'juridico', 'rh', 'ti', 'produtividade', 'imobiliario',
            'ecommerce', 'pessoal', 'geral', 'interno',
          ];

          const allTags = [...new Set(marketplaceAgents.flatMap(a => a.tags || []))].sort();

          const filtered = marketplaceAgents.filter(a => {
            const matchSearch = !agentSearch || a.name?.toLowerCase().includes(agentSearch.toLowerCase()) || a.role?.toLowerCase().includes(agentSearch.toLowerCase());
            const matchTag = !agentTagFilter || (a.tags || []).includes(agentTagFilter);
            return matchSearch && matchTag;
          });

          const grouped = CATEGORY_ORDER.reduce((acc, cat) => {
            const items = filtered.filter(a => (a.category || 'geral') === cat);
            if (items.length > 0) acc[cat] = items;
            return acc;
          }, {});
          // categorias não mapeadas no final
          filtered.forEach(a => {
            const cat = a.category || 'geral';
            if (!CATEGORY_ORDER.includes(cat) && !grouped[cat]) grouped[cat] = [];
            if (!CATEGORY_ORDER.includes(cat)) grouped[cat].push(a);
          });

          return (
            <>
              <AgentFilterBar>
                <AgentSearchInput
                  placeholder={t('agentSearchPlaceholder')}
                  value={agentSearch}
                  onChange={e => setAgentSearch(e.target.value)}
                />
                <AgentTagList>
                  <AgentTagBtn $active={!agentTagFilter} onClick={() => setAgentTagFilter('')}>{t('allTags')}</AgentTagBtn>
                  <AgentTagSelect
                    value={agentTagFilter}
                    onChange={e => setAgentTagFilter(e.target.value)}
                  >
                    <option value="">{t('filterByTag')}</option>
                    {allTags.map(tag => (
                      <option key={tag} value={tag}>{tag}</option>
                    ))}
                  </AgentTagSelect>
                </AgentTagList>
              </AgentFilterBar>

              {filtered.length === 0 ? (
                <EmptyState><EmptyIcon><Bot size={48} strokeWidth={1} /></EmptyIcon><EmptyText>{t('noAgents')}</EmptyText></EmptyState>
              ) : (
                Object.entries(grouped).map(([cat, agents]) => (
                  <AgentGroup key={cat}>
                    <AgentGroupTitle>{CATEGORY_LABELS[cat] || cat}</AgentGroupTitle>
                    <Grid min="300px">
                      {agents.map((agent, idx) => (
                        <MarketCard key={agent.id || `agent-${idx}`} $disabled={agent.enabled === false}>
                          {agent.badge && <CardBadge>{agent.badge}</CardBadge>}
                          <CardIcon><BrandIcon icon={agent.icon || '🤖'} size={44} /></CardIcon>
                          <CardName>{agent.name}</CardName>
                          <CardRole>{agent.role}</CardRole>
                          <CardDesc>{agent.description}</CardDesc>

                          <Features>
                            {(agent.features || []).map((f, i) => <Feature key={i}>✓ {f}</Feature>)}
                          </Features>
                          <CardFooter>
                            <Flex gap={theme.spacing[3]}>
                              <FooterStat>⭐ {agent.rating}</FooterStat>
                              <FooterStat>📥 {agent.installs}</FooterStat>
                              {agent.creditsPerMessage != null && <FooterStat>💰 {t('creditsPerMsg', { count: agent.creditsPerMessage })}</FooterStat>}
                            </Flex>
                          </CardFooter>
                          <Flex gap={theme.spacing[2]}>
                            {editMode ? (
                              <>
                                <Button variant="secondary" size="sm" style={{ flex: 1 }} onClick={() => {
                                  setConfiguringAgent(agent);
                                  setConfiguringAgentForm({
                                    agentId:            agent.agentId,
                                    instanceName:       agent.name,
                                    role:               agent.role || '',
                                    icon:               agent.icon || '🤖',
                                    description:        agent.description || '',
                                    customPrompt:       agent.systemPrompt || '',
                                    basePrompt:         agent.basePrompt || '',
                                    model:              agent.model || '',
                                    temperature:        agent.temperature ?? 0.7,
                                    features:           agent.features || [],
                                    category:           agent.category || 'geral',
                                    tags:               agent.tags || [],
                                    audience:           agent.audience || 'both',
                                    maxTokensPerSession: agent.maxTokensPerSession || '',
                                    sessionLimitMessage: agent.sessionLimitMessage || '',
                                    sessionTimeoutMinutes: agent.sessionTimeoutMinutes || '',
                                    contextWindowSize:  agent.contextWindowSize || '',
                                    maxIterations: agent.maxIterations || '',
                                    usePromptCache:     agent.usePromptCache === true,
                                    enabled:            agent.enabled !== false,
                                  });
                                  setConfiguringTemplateConnectors(agent.templateConnectors || []);
                                }}>{t('config')}</Button>
                                <Button variant={agent.enabled === false ? 'success' : 'warning'} size="sm" onClick={async () => { await api.put(`/marketplace/agents/${agent.agentId}`, { enabled: agent.enabled === false }); api.get('/marketplace/agents').then(r => setMarketplaceAgents(r.data.agents || [])); }}>
                                  {agent.enabled === false ? t('enable') : t('disable')}
                                </Button>
                                <Button variant="danger" size="sm" onClick={() => setDeletingAgent(agent)}><Trash2 size={12} /></Button>
                              </>
                            ) : (
                              <Button variant="secondary" size="sm" style={{ flex: 1 }} onClick={() => {
                                setConfiguringAgent(agent);
                                setConfiguringAgentForm({
                                  agentId:            agent.agentId,
                                  instanceName:       agent.name,
                                  role:               agent.role || '',
                                  icon:               agent.icon || '🤖',
                                  description:        agent.description || '',
                                  customPrompt:       agent.systemPrompt || '',
                                  basePrompt:         agent.basePrompt || '',
                                  model:              agent.model || '',
                                  temperature:        agent.temperature ?? 0.7,
                                  features:           agent.features || [],
                                  category:           agent.category || 'geral',
                                  tags:               agent.tags || [],
                                  audience:           agent.audience || 'both',
                                  maxTokensPerSession: agent.maxTokensPerSession || '',
                                  sessionLimitMessage: agent.sessionLimitMessage || '',
                                  sessionTimeoutMinutes: agent.sessionTimeoutMinutes || '',
                                  contextWindowSize:  agent.contextWindowSize || '',
                                  maxIterations: agent.maxIterations || '',
                                  usePromptCache:     agent.usePromptCache === true,
                                  enabled:            agent.enabled !== false,
                                });
                                setConfiguringTemplateConnectors(agent.templateConnectors || []);
                              }}>{t('details')}</Button>
                            )}
                          </Flex>
                        </MarketCard>
                      ))}
                    </Grid>
                  </AgentGroup>
                ))
              )}
            </>
          );
        })()}

        {activeTab === 'connectors' && (
          connectorTypes.length === 0 ? (
            <EmptyState>
              <EmptyIcon><Plug size={48} /></EmptyIcon>
              <EmptyText>{t('noConnectors')}</EmptyText>
            </EmptyState>
          ) : (
            <Grid min="300px">
              {connectorTypes.map(ct => (
                <MarketCard key={ct.type} $disabled={ct.enabled === false}>
                  {ct.badge && <CardBadge>{ct.badge}</CardBadge>}
                  <CardIcon><BrandIcon icon={ct.icon || '🔌'} size={44} /></CardIcon>
                  <CardName>{lang(ct.name)}</CardName>
                  <CardDesc>{lang(ct.description) || t('connectorTypeAvailable')}</CardDesc>
                  <Flex gap={theme.spacing[1]} style={{ marginBottom: theme.spacing[3], flexWrap: 'wrap' }}>
                    {ct.capabilities?.inbound && <Badge variant="success">INBOUND</Badge>}
                    {ct.capabilities?.outbound && <Badge variant="info">OUTBOUND</Badge>}
                    {ct.capabilities?.scheduled?.length > 0 && <Badge variant="primary">SCHEDULED</Badge>}
                    {ct.system && <Badge variant="default">{t('badgeSystem')}</Badge>}
                    {ct.audience && ct.audience !== 'all' && (
                      <Badge variant={ct.audience === 'business' ? 'warning' : 'primary'}>
                        {ct.audience === 'business' ? 'B2B' : 'B2C'}
                      </Badge>
                    )}
                  </Flex>
                  <CardFooter>
                    <FooterStat>{t('templateFunctions', { count: (ct.capabilities?.tools || []).length })}</FooterStat>
                    <TierPrice>{ct.category || t('integration')}</TierPrice>
                  </CardFooter>
                  <Flex gap={theme.spacing[2]}>
                    {editMode ? (
                      <>
                        <Button variant="secondary" size="sm" style={{ flex: 1 }} onClick={() => setEditingConnectorType(ct)}>{t('edit', { ns: 'common' })}</Button>
                        <Button variant={ct.enabled === false ? 'success' : 'warning'} size="sm" onClick={async () => { await api.put(`/marketplace/connector-types/${ct.type}`, { ...ct, enabled: ct.enabled === false }); api.get('/marketplace/connector-types').then(r => setConnectorTypes(r.data.connectorTypes || [])); }}>
                          {ct.enabled === false ? t('enable') : t('disable')}
                        </Button>
                        <Button variant="danger" size="sm" style={{ flex: 1 }} onClick={() => setDeletingConnectorType(ct)}>{t('delete')}</Button>
                      </>
                    ) : (
                      <Button variant="secondary" size="sm" style={{ flex: 1 }} onClick={() => setViewingFunctions(ct)}>{t('viewFunctions')}</Button>
                    )}
                  </Flex>
                </MarketCard>
              ))}
            </Grid>
          )
        )}

        {activeTab === 'templates' && (
          <>
            {connectorTemplates.length === 0 ? (
              <EmptyState><EmptyIcon><Layout size={48} strokeWidth={1} /></EmptyIcon><EmptyText>{t('connTplEmpty')}</EmptyText></EmptyState>
            ) : (
              <SharedGrid>
                {connectorTemplates.map(tpl => (
                  <SharedCard key={tpl.templateId}>
                    <SharedCardHeader>
                      <SharedIcon><BrandIcon icon={tpl.icon || '🔌'} size={28} /></SharedIcon>
                      <div style={{ flex: 1 }}>
                        <SharedName>{tpl.name}</SharedName>
                        <SharedMeta>{tpl.type}{tpl.displayPhone ? ` · 📱 ${tpl.displayPhone}` : tpl.displayEmail ? ` · 📧 ${tpl.displayEmail}` : ` · ${tpl.templateId}`}</SharedMeta>
                      </div>
                      {editMode && (
                        <Flex gap={theme.spacing[1]}>
                          <Button variant="ghost" size="sm" onClick={() => openEditTemplate(tpl)}><Settings size={13} /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTemplate(tpl)}><Trash2 size={13} /></Button>
                        </Flex>
                      )}
                    </SharedCardHeader>
                    {tpl.description && <SharedDesc>{tpl.description}</SharedDesc>}
                    <Flex gap={theme.spacing[2]} style={{ flexWrap: 'wrap' }}>
                      <Badge variant="info">{tpl.billingMode === 'platform_credits' ? t('billingCredits') : t('billingDirect')}</Badge>
                      {tpl.routingKey && <Badge variant="default">routing: {tpl.routingKey}</Badge>}
                    </Flex>
                    <WebhookRow>
                      <WebhookUrl>{`${process.env.REACT_APP_API_URL}/webhooks/${tpl.type}/${tpl.templateId}`}</WebhookUrl>
                      <CopyBtn onClick={() => copyWebhook(tpl)} title={t('copyWebhookUrl')}>
                        {copiedId === tpl.templateId ? <Check size={12} /> : <Copy size={12} />}
                      </CopyBtn>
                    </WebhookRow>
                  </SharedCard>
                ))}
              </SharedGrid>
            )}

            {templateModal && (() => {
              return (
              <ModalOverlay onClick={() => setTemplateModal(null)}>
                <ModalBox maxWidth="560px" onClick={e => e.stopPropagation()}>
                  <ModalHeader>
                    <ModalTitle>{templateModal === 'create' ? t('connTplNewTitle') : `${t('connTplEditTitle')}: ${templateModal.name}`}</ModalTitle>
                    <Button variant="ghost" size="sm" onClick={() => setTemplateModal(null)}>✕</Button>
                  </ModalHeader>
                  <ModalBody>
                    <FormGroup>
                      <Label style={{ margin: 0 }}>{t('connTplIdLabel')}</Label>
                      <Input value={templateForm.templateId} onChange={e => setTemplateForm(f => ({ ...f, templateId: e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '') }))} placeholder="ex: whatsapp_libben_principal" disabled={templateModal !== 'create'} />
                    </FormGroup>
                    <FormGroup>
                      <Label style={{ margin: 0 }}>{t('connTplTypeLabel')}</Label>
                      <Select value={templateForm.type} onChange={e => setTemplateForm(f => ({ ...f, type: e.target.value }))}>
                        <option value="">{t('selectPlaceholder')}</option>
                        {connectorTypes.map(ct => (
                          <option key={ct.type} value={ct.type}>{lang(ct.icon)} {lang(ct.name)}</option>
                        ))}
                      </Select>
                    </FormGroup>
                    <FormGroup>
                      <Label style={{ margin: 0 }}>{t('connTplNameLabel')}</Label>
                      <Input value={templateForm.name} onChange={e => setTemplateForm(f => ({ ...f, name: e.target.value }))} placeholder="ex: WhatsApp Libben Principal" />
                    </FormGroup>
                    <Flex gap={theme.spacing[3]}>
                      <FormGroup style={{ flex: 1 }}>
                        <Label style={{ margin: 0 }}>{t('connTplIconLabel')}</Label>
                        <Input value={templateForm.icon} onChange={e => setTemplateForm(f => ({ ...f, icon: e.target.value }))} placeholder="🔌" />
                      </FormGroup>
                      <FormGroup style={{ flex: 1 }}>
                        <Label style={{ margin: 0 }}>{t('connTplBillingLabel')}</Label>
                        <Select value={templateForm.billingMode} onChange={e => setTemplateForm(f => ({ ...f, billingMode: e.target.value }))}>
                          <option value="platform_credits">Platform Credits</option>
                          <option value="tenant_direct">Tenant Direct</option>
                        </Select>
                      </FormGroup>
                    </Flex>
                    <FormGroup>
                      <Label style={{ margin: 0 }}>{t('connTplDescLabel')}</Label>
                      <Input value={templateForm.description || ''} onChange={e => setTemplateForm(f => ({ ...f, description: e.target.value }))} placeholder={t('connTplDescPlaceholder')} />
                    </FormGroup>
                    <Flex gap={theme.spacing[3]}>
                      <FormGroup style={{ flex: 1 }}>
                        <Label style={{ margin: 0 }}>{t('connTplPhoneLabel')}</Label>
                        <Input value={templateForm.displayPhone || ''} onChange={e => setTemplateForm(f => ({ ...f, displayPhone: e.target.value }))} placeholder="+55 21 99999-0000" />
                      </FormGroup>
                      <FormGroup style={{ flex: 1 }}>
                        <Label style={{ margin: 0 }}>{t('connTplEmailLabel')}</Label>
                        <Input value={templateForm.displayEmail || ''} onChange={e => setTemplateForm(f => ({ ...f, displayEmail: e.target.value }))} placeholder="contato@empresa.com" />
                      </FormGroup>
                    </Flex>
                    <div style={{ borderTop: `1px solid ${theme.colors.border}`, margin: `${theme.spacing[3]} 0`, paddingTop: theme.spacing[3] }}>
                      <Label style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('connTplCredentialsSection')}</Label>
                    </div>
                    <FormGroup>
                      <Label style={{ margin: 0 }}>{t('connTplRoutingKeyLabel')}</Label>
                      <Input value={templateForm.routingKey || ''} onChange={e => setTemplateForm(f => ({ ...f, routingKey: e.target.value }))} placeholder="phoneNumberId, email address, bot username..." />
                    </FormGroup>
                    <FormGroup>
                      <Label style={{ margin: 0 }}>{t('connTplAccessTokenLabel')}</Label>
                      <PasswordInput type="password" value={templateForm.accessToken || ''} onChange={e => setTemplateForm(f => ({ ...f, accessToken: e.target.value }))} placeholder={t('connTplAccessTokenPlaceholder')} />
                    </FormGroup>
                    <FormGroup>
                      <Label style={{ margin: 0 }}>{t('connTplCredentialsLabel')}</Label>
                      <AdminTextarea rows={4} value={templateForm.credentials || '{}'} onChange={e => setTemplateForm(f => ({ ...f, credentials: e.target.value }))} placeholder='{"appSecret": "...", "smtpHost": "..."}' />
                      <div style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted, marginTop: 4 }}>{t('connTplCredentialsHelp')}</div>
                    </FormGroup>
                  </ModalBody>
                  <ModalFooter>
                    <Button variant="secondary" onClick={() => setTemplateModal(null)}>{t('cancel')}</Button>
                    <Button variant="primary" onClick={handleSaveTemplate} disabled={templateSaving}>
                      {templateSaving ? '...' : t('save')}
                    </Button>
                  </ModalFooter>
                </ModalBox>
              </ModalOverlay>
              );
            })()}
          </>
        )}

        {configuringAgent && (
          <ModalOverlay onClick={() => setConfiguringAgent(null)}>
            <ModalBox maxWidth="900px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle><BrandIcon icon={configuringAgentForm.icon || '🤖'} size={20} /> {configuringAgentForm.instanceName || configuringAgent.name}</ModalTitle>
                <Flex gap={theme.spacing[2]}>
                  <Badge variant={configuringAgentForm.enabled ? 'success' : 'default'}>
                    {configuringAgentForm.enabled ? t('statusActive') : t('statusInactive')}
                  </Badge>
                  <Badge variant="neutral" style={{ fontFamily: 'monospace', fontSize: '11px' }}>{configuringAgentForm.agentId}</Badge>
                  <Button variant="ghost" size="sm" onClick={() => setConfiguringAgent(null)}>✕</Button>
                </Flex>
              </ModalHeader>
              <ModalBody>
                <AgentForm
                  formData={configuringAgentForm}
                  setFormData={setConfiguringAgentForm}
                  isSallusAdmin
                  isConfiguring
                  allConnectorTypes={connectorTypes}
                  sharedConnectors={sharedConnectors}
                  tiers={tiers}
                />
                <TemplateConnectorsSection
                  connectorTypes={connectorTypes}
                  templateConnectors={configuringTemplateConnectors}
                  onChange={setConfiguringTemplateConnectors}
                  lang={lang}
                />
                <div style={{ borderTop: `1px solid ${theme.colors.border}`, margin: `${theme.spacing[4]} 0`, paddingTop: theme.spacing[3] }}>
                  <Label style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t('session.sectionTitle')}</Label>
                </div>
                <Flex gap={theme.spacing[4]}>
                  <FormGroup style={{ flex: 1 }}>
                    <Label style={{ margin: 0 }}>{t('session.tokenLimit')}</Label>
                    <Input type="number" value={configuringAgentForm.maxTokensPerSession || ''} onChange={e => setConfiguringAgentForm(f => ({ ...f, maxTokensPerSession: e.target.value }))} placeholder={t('session.tokenLimitPlaceholder')} />
                  </FormGroup>
                  <FormGroup style={{ flex: 1 }}>
                    <Label style={{ margin: 0 }}>{t('sessionTimeoutLabel')}</Label>
                    <Input type="number" value={configuringAgentForm.sessionTimeoutMinutes || ''} onChange={e => setConfiguringAgentForm(f => ({ ...f, sessionTimeoutMinutes: e.target.value }))} placeholder={t('session.timeoutPlaceholder')} />
                  </FormGroup>
                  <FormGroup style={{ flex: 1 }}>
                    <Label style={{ margin: 0 }}>{t('session.maxIterations')}</Label>
                    <Input type="number" value={configuringAgentForm.maxIterations || ''} onChange={e => setConfiguringAgentForm(f => ({ ...f, maxIterations: e.target.value }))} placeholder="8 (padrão: 8 para tasks, 5 para chat)" min="1" max="30" />
                  </FormGroup>
                  <FormGroup style={{ flex: 1 }}>
                    <Label style={{ margin: 0 }}>{t('session.contextWindow')}</Label>
                    <Input type="number" value={configuringAgentForm.contextWindowSize || ''} onChange={e => setConfiguringAgentForm(f => ({ ...f, contextWindowSize: e.target.value }))} placeholder={t('session.contextWindowPlaceholder')} min="1" max="50" />
                  </FormGroup>
                </Flex>
                <FormGroup>
                  <Label style={{ margin: 0 }}>{t('session.limitMessage')}</Label>
                  <AdminTextarea rows={2} value={configuringAgentForm.sessionLimitMessage || ''} onChange={e => setConfiguringAgentForm(f => ({ ...f, sessionLimitMessage: e.target.value }))} placeholder={t('session.limitMessagePlaceholder')} />
                </FormGroup>
                <FormGroup>
                  <label style={{ display: 'flex', alignItems: 'center', gap: theme.spacing[2], cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={configuringAgentForm.usePromptCache === true}
                      onChange={e => setConfiguringAgentForm(f => ({ ...f, usePromptCache: e.target.checked }))}
                    />
                    <span>
                      <Label style={{ margin: 0 }}>{t('promptCache.label')}</Label>
                      <div style={{ fontSize: theme.typography.xs, color: theme.colors.textMuted }}>
                        {t('promptCache.desc')}
                      </div>
                    </span>
                  </label>
                </FormGroup>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setConfiguringAgent(null)}>{t('cancel')}</Button>
                {editMode && (
                  <Button variant="primary" disabled={configuringAgentSaving} onClick={async () => {
                    setConfiguringAgentSaving(true);
                    try {
                      await api.put(`/marketplace/agents/${configuringAgent.agentId}`, {
                        name:                configuringAgentForm.instanceName,
                        role:                configuringAgentForm.role,
                        icon:                configuringAgentForm.icon,
                        description:         configuringAgentForm.description,
                        systemPrompt:        configuringAgentForm.customPrompt,
                        basePrompt:          configuringAgentForm.basePrompt,
                        model:               configuringAgentForm.model,
                        temperature:         parseFloat(configuringAgentForm.temperature),
                        features:            configuringAgentForm.features,
                        category:            configuringAgentForm.category || 'geral',
                        tags:                configuringAgentForm.tags || [],
                        audience:            configuringAgentForm.audience || 'both',
                        templateConnectors:  configuringTemplateConnectors,
                        maxTokensPerSession: configuringAgentForm.maxTokensPerSession ? parseInt(configuringAgentForm.maxTokensPerSession) : null,
                        sessionLimitMessage: configuringAgentForm.sessionLimitMessage || null,
                        sessionTimeoutMinutes: configuringAgentForm.sessionTimeoutMinutes ? parseInt(configuringAgentForm.sessionTimeoutMinutes) : null,
                        contextWindowSize:   configuringAgentForm.contextWindowSize ? parseInt(configuringAgentForm.contextWindowSize) : null,
                        maxIterations: configuringAgentForm.maxIterations ? parseInt(configuringAgentForm.maxIterations) : null,
                        usePromptCache:      configuringAgentForm.usePromptCache === true,
                        enabled:             configuringAgentForm.enabled,
                      });
                      toast.success(t('configSaved'));
                      setConfiguringAgent(null);
                      api.get('/marketplace/agents').then(r => setMarketplaceAgents(r.data.agents || []));
                    } catch (e) { toast.error(t('error', { msg: e.response?.data?.message || e.message })); }
                    finally { setConfiguringAgentSaving(false); }
                  }}>{configuringAgentSaving ? '...' : t('saveConfig')}</Button>
                )}
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        {deletingAgent && (
          <ModalOverlay onClick={() => setDeletingAgent(null)}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{t('deleteAgentTitle')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setDeletingAgent(null)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <p style={{ margin: 0, fontSize: theme.typography.sm }}>{t('agentDeleteConfirmMsg', { name: deletingAgent.name })}</p>
                <p style={{ margin: `${theme.spacing[2]} 0 0`, fontSize: theme.typography.xs, color: theme.colors.danger }}>{t('agentDeleteWarn')}</p>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setDeletingAgent(null)}>{t('cancel')}</Button>
                <Button variant="danger" onClick={async () => {
                  try {
                    await api.delete(`/marketplace/agents/${deletingAgent.id || deletingAgent.agentId}`);
                    setDeletingAgent(null);
                    window.location.reload();
                  } catch (e) { toast.error(t('error', { msg: e.response?.data?.message || e.message })); }
                }}>{t('delete')}</Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        {viewingFunctions && (
          <ModalOverlay onClick={() => setViewingFunctions(null)}>
            <ModalBox maxWidth="600px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{t('functionsTitle', { name: lang(viewingFunctions.name) })}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setViewingFunctions(null)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                {(viewingFunctions.capabilities?.tools || []).length === 0 ? (
                  <p style={{ fontSize: theme.typography.sm, color: theme.colors.textMuted }}>{t('noFunctions')}</p>
                ) : (
                  (viewingFunctions.capabilities.tools).map((func, idx) => (
                    <FuncItem key={idx}>
                      <FuncName>{lang(func.name) || func.name}</FuncName>
                      <FuncDesc>{lang(func.description) || func.description}</FuncDesc>
                      {func.input_schema && (
                        <pre style={{ background: theme.colors.gray900, color: '#ecf0f1', padding: theme.spacing[3], borderRadius: theme.radii.md, fontSize: '11px', overflow: 'auto', margin: 0 }}>
                          {JSON.stringify(func.input_schema, null, 2)}
                        </pre>
                      )}
                    </FuncItem>
                  ))
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="primary" onClick={() => setViewingFunctions(null)}>{t('close')}</Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        {(editingConnectorType || creatingConnectorType) && (
          <ConnectorTypeForm
            initial={editingConnectorType || null}
            onClose={() => { setEditingConnectorType(null); setCreatingConnectorType(false); }}
            onSaved={() => { setEditingConnectorType(null); setCreatingConnectorType(false); window.location.reload(); }}
          />
        )}

        {deletingConnectorType && (
          <ModalOverlay onClick={() => setDeletingConnectorType(null)}>
            <ModalBox onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{t('deleteConnectorTitle')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setDeletingConnectorType(null)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <p style={{ margin: 0, fontSize: theme.typography.sm }}>{t('connectorDeleteConfirmMsg', { name: deletingConnectorType.name })}</p>
                <p style={{ margin: `${theme.spacing[2]} 0 0`, fontSize: theme.typography.xs, color: theme.colors.danger }}>{t('connectorDeleteWarn')}</p>
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setDeletingConnectorType(null)}>{t('cancel')}</Button>
                <Button variant="danger" onClick={async () => {
                  try {
                    await api.delete(`/marketplace/connector-types/${deletingConnectorType.type}`);
                    setDeletingConnectorType(null);
                    window.location.reload();
                  } catch (e) { toast.error(t('error', { msg: e.response?.data?.message || e.message })); }
                }}>{t('delete')}</Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}

        {creatingAgent && (
          <ModalOverlay onClick={() => setCreatingAgent(false)}>
            <ModalBox maxWidth="640px" onClick={e => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>{t('createAgentTitle')}</ModalTitle>
                <Button variant="ghost" size="sm" onClick={() => setCreatingAgent(false)}>✕</Button>
              </ModalHeader>
              <ModalBody>
                <AgentForm
                  formData={agentForm}
                  setFormData={setAgentForm}
                  isSallusAdmin
                  allConnectorTypes={connectorTypes}
                  sharedConnectors={sharedConnectors}
                  tiers={tiers}
                />
                <TemplateConnectorsSection
                  connectorTypes={connectorTypes}
                  templateConnectors={templateConnectors}
                  onChange={setTemplateConnectors}
                  lang={lang}
                />
              </ModalBody>
              <ModalFooter>
                <Button variant="secondary" onClick={() => setCreatingAgent(false)}>{t('cancel')}</Button>
                <Button variant="primary" onClick={async () => {
                  try {
                    await api.post('/marketplace/agents', {
                      agentId: agentForm.agentId,
                      name: agentForm.instanceName,
                      role: agentForm.role,
                      icon: agentForm.icon,
                      description: agentForm.description,
                      systemPrompt: agentForm.customPrompt,
                      basePrompt: agentForm.basePrompt,
                      model: agentForm.model,
                      temperature: parseFloat(agentForm.temperature),
                      audience: agentForm.audience || 'both',
                      category: agentForm.category || 'geral',
                      tags: agentForm.tags || [],
                      allowedOrigins: agentForm.allowedOrigins || [],
                      enabledActions: agentForm.enabledActions || [],
                      templateConnectors,
                    });
                    setCreatingAgent(false);
                    window.location.reload();
                  } catch (e) { toast.error(t('error', { msg: e.response?.data?.message || e.message })); }
                }}>{t('createAgent')}</Button>
              </ModalFooter>
            </ModalBox>
          </ModalOverlay>
        )}
      </PageContainer>
    </>
  );
};

const BreadcrumbBar = styled.div`background: ${theme.colors.white}; padding: 0 ${theme.spacing[6]}; border-bottom: 1px solid ${theme.colors.border};`;
const TabBar = styled.div`display: flex; gap: 0; margin-bottom: ${theme.spacing[6]}; border-bottom: 2px solid ${theme.colors.border};`;
const Tab = styled.button`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[3]} ${theme.spacing[5]}; background: none; border: none; border-bottom: 3px solid ${p => p.active ? theme.colors.primary : 'transparent'}; color: ${p => p.active ? theme.colors.primary : theme.colors.textSecondary}; font-size: ${theme.typography.sm}; font-weight: ${theme.typography.medium}; cursor: pointer; transition: ${theme.transitions.fast}; margin-bottom: -2px; &:hover { color: ${theme.colors.primary}; }`;
const MarketCard = styled(Card)`display: flex; flex-direction: column; position: relative; opacity: ${p => p.$disabled ? 0.55 : 1}; border-color: ${p => p.$disabled ? theme.colors.border : undefined};`;
const CardBadge = styled.div`position: absolute; top: ${theme.spacing[3]}; right: ${theme.spacing[3]}; background: ${theme.colors.primary}; color: white; padding: 2px ${theme.spacing[2]}; border-radius: ${theme.radii.full}; font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};`;
const CardIcon = styled.div`font-size: 44px; margin-bottom: ${theme.spacing[3]};`;
const CardName = styled.h3`font-size: ${theme.typography.lg}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[1]} 0;`;
const CardRole = styled.div`font-size: ${theme.typography.sm}; color: ${theme.colors.primary}; font-weight: ${theme.typography.medium}; margin-bottom: ${theme.spacing[3]};`;
const CardDesc = styled.p`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted}; line-height: 1.5; margin: 0 0 ${theme.spacing[4]} 0; flex: 1;`;
const Features = styled.div`display: flex; flex-direction: column; gap: ${theme.spacing[1]}; margin-bottom: ${theme.spacing[4]};`;
const Feature = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary};`;
const TiersLabel = styled.div`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textSecondary}; margin-bottom: ${theme.spacing[2]};`;
const TierRow = styled.div`display: flex; justify-content: space-between; align-items: center; padding: ${theme.spacing[2]} ${theme.spacing[3]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.md}; margin-bottom: ${theme.spacing[1]};`;
const TierName = styled.span`font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.primary};`;
const TierLimit = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted}; flex: 1; margin: 0 ${theme.spacing[2]};`;
const TierPrice = styled.span`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const CardFooter = styled.div`display: flex; justify-content: space-between; align-items: center; padding: ${theme.spacing[3]} 0; border-top: 1px solid ${theme.colors.border}; margin: ${theme.spacing[3]} 0; margin-top: auto;`;
const FooterStat = styled.span`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;

const FuncItem = styled.div`padding: ${theme.spacing[4]}; background: ${theme.colors.gray50}; border-radius: ${theme.radii.lg}; border-left: 3px solid ${theme.colors.primary}; display: flex; flex-direction: column; gap: ${theme.spacing[2]};`;
const FuncName = styled.h4`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary}; margin: 0;`;
const FuncDesc = styled.p`font-size: ${theme.typography.sm}; color: ${theme.colors.textMuted}; margin: 0;`;
const Textarea = styled.textarea`width: 100%; padding: ${theme.spacing[2]} ${theme.spacing[3]}; border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.md}; font-size: ${theme.typography.sm}; font-family: inherit; resize: vertical; box-sizing: border-box; outline: none; &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }`;
const AdminTextarea = styled(Textarea)``;
const SharedGrid = styled.div`display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: ${theme.spacing[4]};`;
const SharedCard = styled(Card)`display: flex; flex-direction: column; gap: ${theme.spacing[2]};`;
const SharedCardHeader = styled.div`display: flex; align-items: center; gap: ${theme.spacing[3]};`;
const SharedIcon = styled.div`font-size: 28px; line-height: 1; flex-shrink: 0;`;
const SharedName = styled.div`font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold}; color: ${theme.colors.textPrimary};`;
const SharedMeta = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};`;
const SharedDesc = styled.div`font-size: ${theme.typography.xs}; color: ${theme.colors.textSecondary}; padding-top: ${theme.spacing[1]}; border-top: 1px solid ${theme.colors.border};`;
const WebhookRow = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]}; padding: ${theme.spacing[2]} ${theme.spacing[2]}; background: ${theme.colors.gray900 || '#1e293b'}; border-radius: ${theme.radii.md}; margin-top: ${theme.spacing[1]};`;
const WebhookUrl = styled.div`font-family: monospace; font-size: 10px; color: ${theme.colors.gray400}; flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;`;
const CopyBtn = styled.button`background: none; border: none; cursor: pointer; color: ${theme.colors.gray400}; padding: 2px; display: flex; align-items: center; flex-shrink: 0; &:hover { color: white; }`;

const AgentFilterBar = styled.div`
  display: flex; flex-direction: column; gap: ${theme.spacing[3]};
  margin-bottom: ${theme.spacing[5]};
`;
const AgentSearchInput = styled.input`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border}; border-radius: ${theme.radii.lg};
  font-size: ${theme.typography.sm}; outline: none; width: 320px;
  &:focus { border-color: ${theme.colors.primary}; box-shadow: 0 0 0 3px ${theme.colors.primaryLight}; }
`;
const AgentTagList = styled.div`display: flex; align-items: center; gap: ${theme.spacing[2]};`;
const AgentTagBtn = styled.button`
  padding: 3px ${theme.spacing[3]}; border-radius: ${theme.radii.full};
  border: 1px solid ${p => p.$active ? theme.colors.primary : theme.colors.border};
  background: ${p => p.$active ? theme.colors.primaryLight : 'white'};
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textSecondary};
  font-size: ${theme.typography.xs}; cursor: pointer; white-space: nowrap;
  &:hover { border-color: ${theme.colors.primary}; color: ${theme.colors.primary}; }
`;
const AgentTagSelect = styled.select`
  padding: 3px ${theme.spacing[3]}; border-radius: ${theme.radii.full};
  border: 1px solid ${theme.colors.border};
  background: white; color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.xs}; cursor: pointer; outline: none;
  &:focus { border-color: ${theme.colors.primary}; }
`;
const AgentGroup = styled.div`margin-bottom: ${theme.spacing[8]};`;
const AgentGroupTitle = styled.h3`
  font-size: ${theme.typography.base}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary}; margin: 0 0 ${theme.spacing[4]} 0;
  padding-bottom: ${theme.spacing[2]}; border-bottom: 2px solid ${theme.colors.border};
`;

export default SallusMarketplace;

// ── TemplateConnectorsSection ─────────────────────────────────────────────────
// Permite ao sallus_admin definir quais conectores serão criados automaticamente
// (sem credenciais) quando um tenant instalar este agente.

function TemplateConnectorsSection({ connectorTypes, templateConnectors, onChange, lang }) {
  const { t } = useTranslation('marketplace');
  const [adding, setAdding] = useState(false);
  const [editingTools, setEditingTools] = useState(null); // type do conector sendo editado

  const add = (ct) => {
    if (templateConnectors.find(t => t.type === ct.type)) return;
    onChange([...templateConnectors, { connectorId: ct.type, type: ct.type, name: lang(ct.name), enabledTools: [] }]);
    setAdding(false);
  };

  const remove = (type) => onChange(templateConnectors.filter(t => t.type !== type));

  const setTools = (type, tools) =>
    onChange(templateConnectors.map(t => t.type === type ? { ...t, enabledTools: tools } : t));

  return (
    <div style={{ marginTop: 24, borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
      <Label style={{ fontSize: 11, color: theme.colors.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', margin: '0 0 4px 0', display: 'block' }}>
        {t('templateConnectors.title')}
      </Label>
      <p style={{ fontSize: 12, color: theme.colors.textMuted, margin: '0 0 12px 0' }}>
        {t('templateConnectors.desc')}
      </p>

      {templateConnectors.map(tc => {
        const ct = connectorTypes.find(c => c.type === tc.type);
        const allTools = ct?.capabilities?.tools || [];
        const isExpanded = editingTools === tc.type;
        return (
          <div key={tc.type} style={{ marginBottom: 6, border: '1px solid #bbf7d0', borderRadius: 8, background: theme.colors.successLight, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px' }}>
              <BrandIcon icon={ct ? lang(ct.icon) : '🔌'} size={18} />
              <span style={{ flex: 1, fontSize: 13, fontWeight: 500 }}>{tc.name || lang(ct?.name) || tc.type}</span>
              <span style={{ fontSize: 11, color: theme.colors.textMuted, fontFamily: 'monospace' }}>{tc.type}</span>
              {allTools.length > 0 && (
                <button
                  onClick={() => setEditingTools(isExpanded ? null : tc.type)}
                  style={{ background: 'none', border: '1px solid #86efac', borderRadius: 6, cursor: 'pointer', fontSize: 11, color: theme.colors.success, padding: '2px 8px' }}
                >
                  {tc.enabledTools?.length > 0 ? `${tc.enabledTools.length} tools` : 'tools'} {isExpanded ? '▲' : '▼'}
                </button>
              )}
              <button onClick={() => remove(tc.type)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: theme.colors.danger, fontSize: 16, lineHeight: 1, padding: '0 2px' }}>✕</button>
            </div>

            {isExpanded && allTools.length > 0 && (
              <div style={{ borderTop: '1px solid #bbf7d0', padding: '8px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                <div style={{ fontSize: 11, color: theme.colors.textMuted, marginBottom: 4 }}>{t('templateConnectors.selectTools')}</div>
                {allTools.map(tool => {
                  const checked = (tc.enabledTools || []).includes(tool.name);
                  return (
                    <label key={tool.name} style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer', fontSize: 12 }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={e => {
                          const next = e.target.checked
                            ? [...(tc.enabledTools || []), tool.name]
                            : (tc.enabledTools || []).filter(n => n !== tool.name);
                          setTools(tc.type, next);
                        }}
                        style={{ marginTop: 2, flexShrink: 0 }}
                      />
                      <div>
                        <div style={{ fontWeight: 500, color: theme.colors.textPrimary, fontFamily: 'monospace', fontSize: 11 }}>{tool.name}</div>
                        <div style={{ color: theme.colors.textMuted, fontSize: 11 }}>{tool.description}</div>
                      </div>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}

      {adding ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 8 }}>
          {connectorTypes
            .filter(ct => !templateConnectors.find(t => t.type === ct.type))
            .map(ct => (
              <div
                key={ct.type}
                onClick={() => add(ct)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 10px', background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' }}
              >
                <BrandIcon icon={lang(ct.icon) || '🔌'} size={18} />
                <span style={{ flex: 1, fontSize: 13 }}>{lang(ct.name)}</span>
                <span style={{ fontSize: 11, color: theme.colors.gray400, fontFamily: 'monospace' }}>{ct.type}</span>
              </div>
            ))
          }
          <button onClick={() => setAdding(false)} style={{ marginTop: 4, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: theme.colors.textMuted }}>{t('cancel')}</button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', background: 'white', border: '1px dashed #d1d5db', borderRadius: 8, cursor: 'pointer', fontSize: 13, color: theme.colors.textSecondary }}
        >
          <Plus size={13} /> {t('templateConnectors.add')}
        </button>
      )}
    </div>
  );
}
